import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from "@/lib/supabase"
import { useAuth } from '@/components/auth-context'
import { offlineDB, type OfflineTimeEntry } from "@/lib/offline-db"
import { differenceInSeconds, differenceInHours, format, subDays, startOfDay } from "date-fns"
import type { TimeEntry, Pause } from '@/types/time-tracking'
import { projectKeys } from '@/hooks/queries/use-projects'
import { taskKeys } from '@/hooks/use-tasks'

export const timeKeys = {
    all: ['time-entries'] as const,
    active: (userId: string | undefined, orgId: string | undefined) => [...timeKeys.all, 'active', userId, orgId] as const,
    history: (userId: string | undefined, orgId: string | undefined) => [...timeKeys.all, 'history', userId, orgId] as const,
}

// Retorna el active entry (o paused) y sus pausas
export function useActiveTimeEntryQuery() {
    const { user, organization } = useAuth()

    return useQuery({
        queryKey: timeKeys.active(user?.id, organization?.id),
        queryFn: async () => {
            if (!user || !organization) return { entry: null, pauses: [], zombie: null }

            // Buscamos en remoto
            const { data: remoteEntries, error } = await supabase
                .from("time_entries")
                .select("*, projects(name, color, client_id), tasks(name)")
                .eq("user_id", user.id)
                .eq("organization_id", organization.id)
                .in("status", ["active", "paused"])
                .order("clock_in", { ascending: false })
                .limit(5)

            if (error) console.error("Error fetching active entries:", error)

            if (remoteEntries && remoteEntries.length > 0) {
                const data = remoteEntries[0]

                // Limpiar extras
                if (remoteEntries.length > 1) {
                    const now = new Date().toISOString()
                    for (let i = 1; i < remoteEntries.length; i++) {
                        const extra = remoteEntries[i]
                        const totalHours = differenceInSeconds(new Date(), new Date(extra.clock_in)) / 3600
                        await supabase.from("time_entries").update({ clock_out: now, status: "completed", total_hours: Number(totalHours.toFixed(2)) }).eq("id", extra.id)
                    }
                }

                // Auto-cierre de sesión huérfana: el proyecto fue eliminado permanentemente
                if (data.project_id && !data.projects) {
                    const now = new Date().toISOString()
                    const totalHours = differenceInSeconds(new Date(), new Date(data.clock_in)) / 3600
                    await supabase.from("time_entries").update({
                        clock_out: now,
                        status: "completed",
                        total_hours: Number(totalHours.toFixed(2))
                    }).eq("id", data.id)
                    try { await offlineDB.timeEntries.delete(data.id) } catch { /* ignorar */ }
                    return { entry: null, pauses: [], zombie: null }
                }

                const startTime = new Date(data.clock_in)
                const hoursRunning = differenceInHours(new Date(), startTime)
                const isZombie = hoursRunning >= 12 && data.status !== 'paused'

                if (isZombie) {
                    return { entry: null, pauses: [], zombie: data as unknown as TimeEntry }
                }

                const normalized = { ...data, project: data.projects, task: data.tasks }

                const { data: pausesData } = await supabase.from("pauses")
                    .select("*")
                    .eq("time_entry_id", data.id)
                    .order("start_time", { ascending: true })

                const currentPauses = (pausesData || []) as Pause[]

                // Guardar local para offline support
                try {
                    await offlineDB.timeEntries.put({ ...data, synced: true, last_updated: new Date().toISOString() } as unknown as OfflineTimeEntry)
                } catch (e) { console.error("Error offline tracking", e) }

                return { entry: normalized as unknown as TimeEntry, pauses: currentPauses, zombie: null }
            } else {
                // SI NO HAY REMOTO (y estamos online), limpiar cualquier rastro de timer activo local
                // Esto evita los "ghost timers" cuando detienes el tiempo en otro dispositivo
                try {
                    const localActives = await offlineDB.timeEntries
                        .where('status')
                        .anyOf(['active', 'paused'])
                        .toArray()

                    if (localActives.length > 0) {
                        // Si hay registros locales activos pero Supabase dice que no, 
                        // es que se cerraron en otro sitio. Los marcamos como completados localmente.
                        for (const entry of localActives) {
                            await offlineDB.timeEntries.update(entry.id, {
                                status: 'completed',
                                synced: true
                            })
                        }
                    }
                } catch (e) { console.error("Error cleaning ghost timers", e) }

                return { entry: null, pauses: [], zombie: null }
            }
        },
        enabled: !!user && !!organization,
        refetchOnWindowFocus: true,
        refetchInterval: 30000,
    })
}


// Mutaiones:
export function useClockInMutation() {
    const queryClient = useQueryClient()
    const { user, organization } = useAuth()

    return useMutation({
        mutationFn: async ({ projectId, taskId, activeEntry }: { projectId?: string, taskId?: string, activeEntry: TimeEntry | null }) => {
            if (!user || !organization) throw new Error("Unauthenticated")

            // Cerrar el timer actual si hay uno
            if (activeEntry) {
                // Not ideal to have side-effect clock-out here, better logic in use-time-tracking
                // But handled for safety
            }

            // Limpiar zombies
            const { data: zombieEntries } = await supabase.from("time_entries").select("id, clock_in").eq("user_id", user.id).in("status", ["active", "paused"])
            if (zombieEntries && zombieEntries.length > 0) {
                const now = new Date().toISOString()
                for (const zombie of zombieEntries) {
                    const totalHours = differenceInSeconds(new Date(), new Date(zombie.clock_in)) / 3600
                    await supabase.from("time_entries").update({ clock_out: now, status: "completed", total_hours: Number(totalHours.toFixed(2)) }).eq("id", zombie.id)
                }
            }

            const now = new Date().toISOString()
            const entryData = {
                user_id: user.id,
                organization_id: organization.id,
                project_id: projectId || null,
                task_id: taskId || null,
                clock_in: now,
                date: now.split("T")[0],
                status: "active" as const
            }

            const { data, error } = await supabase.from("time_entries").insert(entryData).select('*, projects(name, color, client_id), tasks(name)').maybeSingle()

            if (error) {
                // Offline fallback
                const localId = await offlineDB.timeEntries.add({ ...entryData, clock_out: null, total_hours: null, synced: false, last_updated: now })
                return { ...entryData, id: String(localId), clock_out: null, total_hours: null, offline: true } as unknown as TimeEntry
            }

            if (data) {
                const normalized = { ...data, project: data.projects, task: data.tasks }
                await offlineDB.timeEntries.put({ ...data, synced: true, last_updated: now } as unknown as OfflineTimeEntry)
                return normalized as unknown as TimeEntry
            }

            return null
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: timeKeys.active(user?.id, organization?.id) })
            queryClient.invalidateQueries({ queryKey: projectKeys.all })
            queryClient.invalidateQueries({ queryKey: taskKeys.all })
        }
    })
}

export function useClockOutMutation() {
    const queryClient = useQueryClient()
    const { user, organization } = useAuth()

    return useMutation({
        mutationFn: async ({ activeEntry, pauses }: { activeEntry: TimeEntry, pauses: Pause[] }) => {
            const now = new Date().toISOString()
            const start = new Date(activeEntry.clock_in)
            let totalPauseSeconds = 0
            pauses.forEach(p => {
                const pStart = new Date(p.start_time)
                const pEnd = p.end_time ? new Date(p.end_time) : new Date(now)
                totalPauseSeconds += differenceInSeconds(pEnd, pStart)
            })
            const totalHours = Number(((differenceInSeconds(new Date(now), start) - totalPauseSeconds) / 3600).toFixed(2))

            const { error } = await supabase.from("time_entries").update({ clock_out: now, status: "completed", total_hours: totalHours }).eq("id", activeEntry.id)

            await offlineDB.timeEntries.where('user_id').equals(user?.id || '').and(e => e.clock_in === activeEntry.clock_in).modify({ clock_out: now, status: 'completed', total_hours: totalHours, synced: !error, last_updated: now } as unknown as OfflineTimeEntry)

            if (error) throw new Error("Offline clockout")
            return true
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: timeKeys.active(user?.id, organization?.id) })
            queryClient.invalidateQueries({ queryKey: projectKeys.all })
            queryClient.invalidateQueries({ queryKey: taskKeys.all })
            queryClient.invalidateQueries({ queryKey: timeKeys.history(user?.id, organization?.id) }) // update reports
        }
    })
}

export function useTogglePauseMutation() {
    const queryClient = useQueryClient()
    const { user, organization } = useAuth()

    return useMutation({
        mutationFn: async ({ activeEntry }: { activeEntry: TimeEntry }) => {
            const now = new Date().toISOString()
            const newStatus = activeEntry.status === "active" ? "paused" : "active"

            if (newStatus === "paused") {
                await supabase.from("pauses").insert({ time_entry_id: activeEntry.id, start_time: now, type: "break" })
            } else {
                await supabase.from("pauses").update({ end_time: now }).eq("time_entry_id", activeEntry.id).is("end_time", null)
            }

            const { error } = await supabase.from("time_entries").update({ status: newStatus }).eq("id", activeEntry.id)
            if (error) throw error
            return newStatus
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: timeKeys.active(user?.id, organization?.id) })
            queryClient.invalidateQueries({ queryKey: projectKeys.all })
            queryClient.invalidateQueries({ queryKey: taskKeys.all })
            queryClient.invalidateQueries({ queryKey: timeKeys.all })
        }
    })
}

export function useDashboardStatsQuery() {
    const { user, organization } = useAuth()

    return useQuery({
        queryKey: [...timeKeys.all, 'history-stats', user?.id, organization?.id],
        queryFn: async () => {
            if (!user || !organization) return { recent: [], weeklyHistory: [] }

            const sevenDaysAgo = new Date()
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6)
            sevenDaysAgo.setHours(0, 0, 0, 0)

            const [recentRes, historyRes, profileRes] = await Promise.all([
                supabase
                    .from('time_entries')
                    .select('*, projects(name, color, id), tasks(name, id)')
                    .eq('user_id', user.id)
                    .eq('organization_id', organization.id)
                    .order('clock_in', { ascending: false })
                    .limit(50),
                supabase
                    .from('time_entries')
                    .select('total_hours, clock_in')
                    .eq('user_id', user.id)
                    .eq('organization_id', organization.id)
                    .gte('clock_in', sevenDaysAgo.toISOString()),
                supabase
                    .from('profiles')
                    .select('current_streak, max_streak, badges')
                    .eq('id', user.id)
                    .single()
            ])

            if (recentRes.error) throw recentRes.error
            if (historyRes.error) throw historyRes.error

            return {
                recent: recentRes.data || [],
                weeklyHistory: historyRes.data || [],
                profileStats: profileRes.data || { current_streak: 0, max_streak: 0, badges: [] }
            }
        },
        enabled: !!user && !!organization
    })
}

export function useHistoryQuery(
    dateFilter: "7days" | "30days" | "90days" | "all",
    page: number,
    itemsPerPage: number,
    isAggregationEnabled: boolean
) {
    const { user, organization } = useAuth()

    return useQuery({
        queryKey: [...timeKeys.all, 'history', organization?.id, dateFilter, page, itemsPerPage, isAggregationEnabled],
        queryFn: async () => {
            if (!user || !organization) return { entries: [], totalCount: 0 }

            let query = supabase
                .from('time_entries')
                .select(`
                    *,
                    projects(name, color),
                    tasks(name)
                `, { count: 'exact' })
                .eq('user_id', user.id)
                .eq('organization_id', organization.id)
                .order('clock_in', { ascending: false })

            if (dateFilter !== "all") {
                const days = dateFilter === "7days" ? 7 : dateFilter === "30days" ? 30 : 90
                const startDate = startOfDay(subDays(new Date(), days))
                query = query.gte('clock_in', startDate.toISOString())
            }

            const from = (page - 1) * itemsPerPage
            const to = from + itemsPerPage - 1
            query = query.range(from, to)

            const { data, error, count } = await query

            if (error) throw error
            if (!data) return { entries: [], totalCount: 0 }

            let formattedData = data.map(entry => ({
                ...entry,
                project_name: (entry.projects as { name?: string })?.name || "Sin Proyecto",
                task_name: (entry.tasks as { name?: string })?.name || "Sin Tarea"
            }))

            if (isAggregationEnabled) {
                const aggregated: Record<string, typeof formattedData[0] & { aggregatedIds: string[] }> = {}
                formattedData.forEach(entry => {
                    const day = format(new Date(entry.clock_in), 'yyyy-MM-dd')
                    const key = `${day}-${entry.project_id}-${entry.task_id}`

                    if (aggregated[key]) {
                        // Acumular horas Y trackear todos los IDs del grupo
                        aggregated[key].total_hours = (aggregated[key].total_hours || 0) + (entry.total_hours || 0)
                        aggregated[key].aggregatedIds.push(entry.id)
                    } else {
                        aggregated[key] = { ...entry, aggregatedIds: [entry.id] }
                    }
                })
                formattedData = Object.values(aggregated).sort((a, b) =>
                    new Date(b.clock_in).getTime() - new Date(a.clock_in).getTime()
                )
            }

            return {
                entries: formattedData,
                totalCount: count || 0
            }
        },
        enabled: !!user && !!organization
    })
}

export function useDeleteTimeEntryMutation() {
    const queryClient = useQueryClient()

    return useMutation({
        // Acepta un array de IDs para borrar entradas agregadas de una sola vez
        mutationFn: async (ids: string[]) => {
            if (ids.length === 0) throw new Error('No hay IDs para eliminar.')

            const { error, count } = await supabase
                .from('time_entries')
                .delete({ count: 'exact' })
                .in('id', ids)

            if (error) throw error

            // count === 0 indica fallo silencioso de RLS (sin permisos o ya no existen)
            if (count === 0) {
                throw new Error('No se pudo eliminar el registro. Sin permisos o ya no existe.')
            }

            // Limpiar todos los IDs del grupo de la caché offline
            await offlineDB.timeEntries.bulkDelete(ids)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: timeKeys.all })
            queryClient.invalidateQueries({ queryKey: projectKeys.all })
            queryClient.invalidateQueries({ queryKey: taskKeys.all })
        }
    })
}
